import { memo } from 'react';
import type { CashBookEntryDTO, CashBookEntryKind } from '@six-pockets/shared';
import { cx } from '@/utils/cx';
import { formatDateTime, formatINR } from '@/utils/format';
import { SOURCE_LABEL } from '../sources';
import styles from '@/styles/components/LedgerRow/style.scss';

const KIND_LABEL: Record<CashBookEntryKind, string> = {
  count: 'Cash count',
  deposit: 'Deposit to bank',
  expense: 'Expense',
  withdrawal: 'Withdrawal',
};

/** "+₹500", "−₹600", "₹0". */
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${formatINR(Math.abs(n))}`;

/** One passbook line: what happened and when · what it did to the money at the counter · the balance after it. */
export const CashBookRow = memo(function CashBookRow({ entry: e }: { entry: CashBookEntryDTO }) {
  const title = e.reason ?? KIND_LABEL[e.kind];

  return (
    <li className={styles.row}>
      <div className={styles.main}>
        <span className={styles.title} title={title}>
          {title}
        </span>
        <span className={styles.meta}>
          {e.kind === 'expense' && <span className={styles.source}>{KIND_LABEL.expense}</span>}
          {e.kind === 'withdrawal' && (
            <span className={cx(styles.source, styles.person)}>
              {KIND_LABEL.withdrawal}
              {e.by && ` · ${SOURCE_LABEL[e.by]}`}
            </span>
          )}
          {formatDateTime(e.at)}
        </span>
      </div>
      {/* The first count has nothing before it to compare with. */}
      <span className={cx(styles.change, e.change !== null && e.change > 0 && styles.in, e.change !== null && e.change < 0 && styles.out)}>
        {e.change === null ? 'Opening' : signed(e.change)}
      </span>
      <span className={styles.balance}>
        <span className={styles.balanceLabel}>Balance</span>
        {formatINR(e.balance)}
      </span>
    </li>
  );
});
